// @flow strict

import { Message } from '@ansarada/ace-react';
import type { Group } from '@ansarada/ace-react/dist/ace/components/Select2/Select2.types';
import axios from 'axios';
import queryString from 'query-string';
import * as React from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { getChargebeeData } from '../../helpers';
import PureQuestionSubmit from './PureQuestionSubmit';
import type { ProvisioningData } from '../../types';

type PropsType = {|
  onBack: () => void,
  onGoToProfilePage: (data: ProvisioningData) => void,
  onGoToFirstQuestion?: () => void,
  additionalInformationPlaceholder: string,
  question1: string,
  question2: string,
  question2Part2: string,
  question2Part3: string,
  question3: string,
  additionalInformationLabel: string,
  customerId: string,
  bgMobile: string,
  bgDesktop: string,
  question4Text: string,
  outcome: string,
  pathwayId: ?number,
  question1Title: string,
  question1Placeholder: string,
  question1Options: Array<Group>,
  isFromReferrer: boolean,
  enabledTransactionTypeIndustryQuestions: boolean,
|};

const Question4Submit = ({
  onBack,
  onGoToProfilePage,
  additionalInformationPlaceholder,
  question1,
  question2,
  question2Part2,
  question2Part3,
  question3,
  additionalInformationLabel,
  bgMobile,
  bgDesktop,
  customerId,
  onGoToFirstQuestion,
  question4Text,
  outcome,
  pathwayId,
  question1Title,
  question1Placeholder,
  question1Options,
  isFromReferrer,
  enabledTransactionTypeIndustryQuestions,
}: PropsType) => {
  const title = question4Text.replace('[outcome]', outcome);
  const [customerType, setCustomerType] = React.useState('');
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [isSubmitting, setSubmitting] = React.useState(false);
  const [additionalInformationValue, setAdditionalInformationValue] = React.useState('');
  const [recaptchaFailed, setRecaptchaFailed] = React.useState(false);

  const handleSubmit = async () => {
    setSubmitting(true);
    setRecaptchaFailed(false);
    const chargebeeData = getChargebeeData({
      enabledTransactionTypeIndustryQuestions,
      question1: question1 || customerType,
      question2,
      question3,
      question2Part2,
      question2Part3,
      question4: additionalInformationValue,
      outcome,
      pathwayId,
    });

    analytics.track('Onboarding Questions start my free trial', {
      location: 'pricing_page',
      question1: question1 || customerType,
      question2,
      question2Part2,
      question2Part3,
      question3,
      question4: additionalInformationValue,
    });
    const recaptchaAction = 'OnBoardingSaveSubscription';
    const recaptchaToken = await executeRecaptcha(recaptchaAction);
    const params = queryString.parse(window.location.search);

    try {
      const response = await axios.post(`${window.location.origin}/free-trial/provisioning`, {
        dto: {
          ...chargebeeData,
          CustomerId: customerId,
          RecaptchaToken: recaptchaToken,
          RecaptchaAction: recaptchaAction,
          UtmSource: params.utm_source,
          UtmMedium: params.utm_medium,
          UtmCampaign: params.utm_campaign,
          ProvisioningType: 'FreeTrial',
        },
      });

      if (response.data.Status === 0) {
        onGoToProfilePage({
          sessionId: response.data.SessionId,
          signupToken: response.data.SignupToken,
          isProfileExists: response.data.IsProfileExists,
        });
      } else if (response.data.Status === 3) {
        setRecaptchaFailed(true);
      } else {
        window.location.href = '/500';
      }
    } catch (error) {
      window.location.href = '/500';
    }
    setSubmitting(false);
  };

  return (
    <>
      <PureQuestionSubmit
        title={title}
        onBack={onBack}
        onNext={() => {
          handleSubmit();
        }}
        onGoToFirstQuestion={onGoToFirstQuestion}
        additionalInformationPlaceholder={additionalInformationPlaceholder}
        question1={question1}
        question2={question2}
        question2Part2={question2Part2}
        question2Part3={question2Part3}
        question3={question3}
        additionalInformationLabel={additionalInformationLabel}
        additionalInformationValue={additionalInformationValue}
        setAdditionalInformationValue={setAdditionalInformationValue}
        bgMobile={bgMobile}
        bgDesktop={bgDesktop}
        isSubmitting={isSubmitting}
        isFromReferrer={isFromReferrer}
        question1Title={question1Title}
        question1Placeholder={question1Placeholder}
        question1Options={question1Options}
        customerType={customerType}
        setCustomerType={setCustomerType}
      />
      {recaptchaFailed && (
        <Message variant="criticalerror" context="page">
          {/* eslint-disable-next-line react/no-unescaped-entities */}
          That didn't work. Press 'Start my free trial' again.
        </Message>
      )}
    </>
  );
};

export default Question4Submit;
